<script setup lang="ts">
import type { Component, Page } from '@bloomreach/spa-sdk'
import { toRefs } from 'vue'
import type { TInstallmentOffers, TProducts, TRetailOffers } from './ProductsGridTypes'
import ProductsGridItem from './ProductsGridItem.vue'

interface IItemTab {
    label: string
    content: string
    slot: string
}

/**
 * SDX
 */
const props = defineProps<{
    component: Component
    page: Page
}>()
const { component, page } = toRefs(props)
const documentRef = computed(() => component.value.getModels().document)
const document = computed(() => documentRef.value && page.value.getContent(documentRef.value))
const data = computed(() => document.value?.getData())
const { title, filters, products } = data.value

/**
 * CUSTOM SOLUTION
 */
const styleGrid = 'm-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3'
const itemsTab = ref<IItemTab[]>([])
const offers = splitIntoCashAndInstallments(products)
filters.forEach((el: { title: string; identifier: string }) => {
    itemsTab.value.push({
        label: el.title,
        content: '',
        slot: el.identifier,
    })
})

function getOffers(slot: string) {
    if (slot === 'RETAIL') {
        return offers.retailOffers
    }

    if (slot === 'INSTALLMENTS') {
        return offers.installmentOffers
    }
    return []
}

const uiTab = {
    list: {
        background: 'bg-neutral-100',
        tab: {
            active: 'text-neutral-primary',
            inactive: 'text-neutral-primary ',
            font: ' font-bold',
        },
        marker: {
            background: 'bg-brand-accent',
        },
    },
}

function splitIntoCashAndInstallments(phones: TProducts[]) {
    const retailOffers: TRetailOffers[] = []
    const installmentOffers: TInstallmentOffers[] = []

    if (!phones?.length) {
        return { retailOffers, installmentOffers }
    }

    phones.forEach((phone) => {
        const basicData = {
            name: phone.productName,
            cta: phone.actionTitle,
            image: phone.productPhoto,
        }

        if (phone?.RETAIL?.productPrice) {
            retailOffers.push({
                ...basicData,
                price: phone.RETAIL.productPrice,
                omnibusText: phone.RETAIL.omnibusText,
            })
        }

        if (phone.INSTALLMENTS && phone.INSTALLMENTS.length) {
            installmentOffers.push({
                ...basicData,
                installmentNoLabel: phone.installmentNoLabel,
                installmentStart: phone.INSTALLMENTS[0].installmentNo,
                installments: phone.INSTALLMENTS.map((installment) => ({
                    initialPrice: installment.initialPrice,
                    omnibusText: installment.omnibusText,
                    hardwarePrice: installment.hardwarePrice,
                    installmentNo: installment.installmentNo,
                    installmentPrice: installment.installmentPrice,
                    installmentPriceSuffixText: installment.installmentPriceSuffixText,
                })),
            })
        }
    })

    return { retailOffers, installmentOffers }
}

const selected = ref('INSTALLMENTS')
const options = computed(() => {
    const array = []
    for (const filtr of filters) {
        array.push({
            label: filtr.title,
            value: filtr.identifier,
        })
    }
    return array
})
</script>

<template>
    <section class="mx-auto max-w-7xl bg-black px-4 py-6">
        <h3
            class="my-6 text-center text-3xl text-white"
            v-html="title"
        />
        <UTabs
            :items="itemsTab"
            class="w-full"
            :ui="uiTab"
        >
            <template
                v-for="(item, index) in itemsTab"
                :key="index"
                #[item.slot]="{ item: item }"
            >
                <p class="text-white">{{ item.content }}</p>
                <section :class="styleGrid">
                    <ProductsGridItem
                        v-for="(offer, key) in getOffers(item.slot)"
                        :key="key"
                        :data="offer"
                    />
                </section>
            </template>
        </UTabs>
    </section>

    <section class="mx-auto max-w-7xl bg-black px-4 py-6">
        <h3
            class="my-6 text-center text-3xl text-white"
            v-html="title"
        />
        <URadioGroup
            v-model="selected"
            :options="options"
            color="yellow"
            class="offer-selected"
            :class="selected.toLowerCase()"
            :ui="{
                form: 'border-4 border-brand-accent',
                fieldset: 'flex flex-row flex-wrap justify-center md:flex-nowrap w-full gap-5',
            }"
        />
        <section :class="styleGrid">
            <ProductsGridItem
                v-for="(offer, key) in getOffers(selected)"
                :key="key"
                :data="offer"
            />
        </section>
    </section>
</template>

<style lang="scss" scoped>
.offer-selected :deep(fieldset) {
    > div {
        @apply border-2 border-white p-3 px-6 rounded-[20px] items-center;
        label {
            @apply text-xl font-bold text-white;
        }
    }
}

.retail :deep(fieldset) {
    & > div:nth-child(2) {
        @apply border-2 border-brand-accent;
        label {
            @apply text-brand-accent;
        }
    }
}

.installments :deep(fieldset) {
    & > div:nth-child(1) {
        @apply border-2 border-brand-accent;
        label {
            @apply text-brand-accent;
        }
    }
}
</style>
